import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "./printApi";
import SideNavLayout from "../customsidenav/sidenavlayout";
import ToastMessage from "../utils/mui/righttoptoast";
import { buildSheet, buildDocument } from "./encounterForm";
import { printHtmlDocument } from "./printUtils";
import {
  todayStr,
  fmtDateSlash,
  fmtSex,
  fmtName,
  fmtTime,
  ageFromDob,
  loadEncounterClinical,
} from "./encounterData";
import { DateField } from "../components/calendar/CalendarPanel";
import { getPracticeId } from "../config/practice";

const practiceid = getPracticeId();

/* ------------------------------- component ------------------------------- */

const PrintNotes = () => {
  const [tab, setTab] = useState("appointments"); // 'appointments' | 'patient'

  // Appointment mode
  const [date, setDate] = useState(todayStr());
  const [providerFilter, setProviderFilter] = useState("ALL");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Patient-search mode
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Async UI state
  const [busyKey, setBusyKey] = useState(null); // which row/action is working
  const [busyAll, setBusyAll] = useState(null); // 'print' | null
  const [preview, setPreview] = useState(null); // resolved form data for modal

  const clinicalCache = useRef({}); // patientid -> {allergies, medications, partial}

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const closeToast = (_e, reason) => {
    if (reason !== "clickaway") setToast((t) => ({ ...t, open: false }));
  };

  /* ----------------------------- data loads ----------------------------- */

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post(`/v1/${practiceid}/scheduler/schedule_list`, {
        startdate: date,
        enddate: date,
      });
      const data = res && res.status === 200 && Array.isArray(res.data) ? res.data : [];
      data.sort((a, b) => String(a.starttime || "").localeCompare(String(b.starttime || "")));
      setAppointments(data);
    } catch (err) {
      console.error("schedule_list failed", err);
      showToast("Failed to load appointments", "error");
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    if (tab === "appointments") fetchAppointments();
  }, [fetchAppointments, tab]);

  const doSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const res = await api.post(`/v1/${practiceid}/patient/lookup`, { lookuptext: q });
      const data = res && res.status === 200 && Array.isArray(res.data) ? res.data : [];
      setResults(data);
      if (!data.length) showToast("No patients found for that search", "info");
    } catch (err) {
      console.error("patient lookup failed", err);
      showToast("Search failed", "error");
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Fetch allergies + active meds (cached per patient) via the shared loader.
  const fetchClinical = useCallback(async (patientid) => {
    if (!patientid) return { allergies: [], medications: [], partial: false };
    if (clinicalCache.current[patientid]) return clinicalCache.current[patientid];
    const result = await loadEncounterClinical(patientid);
    clinicalCache.current[patientid] = result;
    return result;
  }, []);

  /* --------------------------- derived values --------------------------- */

  const providers = useMemo(
    () => [...new Set(appointments.map((a) => a.providername).filter(Boolean))],
    [appointments]
  );

  const filteredAppointments = useMemo(
    () =>
      providerFilter === "ALL"
        ? appointments
        : appointments.filter((a) => a.providername === providerFilter),
    [appointments, providerFilter]
  );

  /* ----------------------- demographics builders ------------------------ */

  const apptDemographics = useCallback(
    (appt) => ({
      name: fmtName(appt.lastname, appt.firstname),
      dob: fmtDateSlash(appt.dob),
      age: ageFromDob(appt.dob),
      sex: fmtSex(appt.gender),
      mrn: String(appt.patientid || ""),
      provider: appt.providername || "",
      dos: fmtDateSlash(date),
      time: fmtTime(appt.starttime),
      // Booking-time primary insurance from appointment_master — used only as a
      // fallback for the Primary line when FHIR Coverage returns nothing.
      insuranceFallback: appt.insurancename || "",
    }),
    [date]
  );

  const patientDemographics = useCallback(
    (p) => ({
      name: fmtName(p.lastname, p.firstname),
      dob: fmtDateSlash(p.dob),
      age: ageFromDob(p.dob),
      sex: fmtSex(p.gender),
      mrn: String(p.patientid || ""),
      provider: "",
      dos: fmtDateSlash(todayStr()),
      time: "",
    }),
    []
  );

  /* ------------------------------ actions ------------------------------- */

  const resolveForm = async (demographics, patientid) => {
    const clinical = await fetchClinical(patientid);
    if (clinical.partial)
      showToast("Some FHIR data was unavailable — form printed with what loaded", "warning");
    return { ...demographics, ...clinical };
  };

  const openPreview = async (demographics, patientid, key) => {
    setBusyKey(key);
    try {
      const data = await resolveForm(demographics, patientid);
      setPreview(data);
    } catch (err) {
      console.error(err);
      showToast("Could not load patient data", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const printOne = async (demographics, patientid, key) => {
    setBusyKey(key);
    try {
      const data = await resolveForm(demographics, patientid);
      await printHtmlDocument(buildDocument([buildSheet(data)], `Encounter — ${data.name}`));
    } catch (err) {
      console.error(err);
      showToast("Print failed", "error");
    } finally {
      setBusyKey(null);
    }
  };

  // Limited-concurrency map so Print All doesn't fire dozens of requests at once.
  const mapWithConcurrency = async (items, fn, limit = 5) => {
    const out = new Array(items.length);
    let idx = 0;
    const worker = async () => {
      while (idx < items.length) {
        const cur = idx;
        idx += 1;
        out[cur] = await fn(items[cur], cur);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return out;
  };

  const buildAllSheets = async () => {
    const datas = await mapWithConcurrency(filteredAppointments, async (appt) => {
      const clinical = await fetchClinical(appt.patientid);
      return { ...apptDemographics(appt), ...clinical };
    });
    return datas.map((d) => buildSheet(d));
  };

  const printAll = async () => {
    if (!filteredAppointments.length) return;
    setBusyAll("print");
    try {
      const sheets = await buildAllSheets();
      await printHtmlDocument(buildDocument(sheets, `Encounter Forms — ${date}`));
    } catch (err) {
      console.error(err);
      showToast("Print All failed", "error");
    } finally {
      setBusyAll(null);
    }
  };

  /* preview-modal actions reuse the already-resolved data */
  const printPreview = () =>
    printHtmlDocument(buildDocument([buildSheet(preview)], `Encounter — ${preview.name}`));

  /* ------------------------------- render ------------------------------- */

  const btn = "px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed";
  const btnPrimary = `${btn} bg-[#1D2951] text-white hover:bg-[#16203f]`;
  const btnGhost = `${btn} border border-[#1D2951] text-[#1D2951] hover:bg-[#EEF2FB]`;

  return (
    <SideNavLayout>
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-[#1D2951] mr-auto">Print Notes</h1>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setTab("appointments")}
            className={`px-4 py-2 text-sm font-semibold ${tab === "appointments" ? "bg-[#1D2951] text-white" : "bg-white text-gray-600"}`}
          >
            By appointment
          </button>
          <button
            onClick={() => setTab("patient")}
            className={`px-4 py-2 text-sm font-semibold ${tab === "patient" ? "bg-[#1D2951] text-white" : "bg-white text-gray-600"}`}
          >
            By patient
          </button>
        </div>
      </div>

      {tab === "appointments" ? (
        <>
          {/* Appointment controls */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <DateField value={date} onChange={setDate} />
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[160px]"
            >
              <option value="ALL">All providers</option>
              {providers.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button onClick={fetchAppointments} className={btnPrimary}>
              Refresh
            </button>
            <div className="flex-1" />
            <button
              onClick={printAll}
              disabled={!filteredAppointments.length || !!busyAll}
              className={btnPrimary}
            >
              {busyAll === "print" ? "Preparing…" : `Print All (${filteredAppointments.length})`}
            </button>
          </div>

          {/* Appointment list */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b">
              {loading
                ? "Loading…"
                : `${filteredAppointments.length} appointment${filteredAppointments.length === 1 ? "" : "s"}`}
            </div>
            <div className="max-h-[68vh] overflow-y-auto divide-y">
              {!loading && filteredAppointments.length === 0 && (
                <div className="text-gray-400 text-sm p-8 text-center">
                  No appointments for this date.
                </div>
              )}
              {filteredAppointments.map((a) => {
                const key = `appt-${a.appointmentid || a.AID || a.patientid}`;
                const working = busyKey === key;
                const demo = apptDemographics(a);
                return (
                  <div key={key} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    <button
                      className="flex-1 text-left"
                      onClick={() => openPreview(demo, a.patientid, key)}
                      title="Preview form"
                    >
                      <div className="font-semibold text-sm text-gray-800">
                        {a.patientName || fmtName(a.lastname, a.firstname)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {fmtTime(a.starttime)}
                        {a.providername ? ` · ${a.providername}` : ""}
                        {a.type ? ` · ${a.type}` : ""}
                        {` · MRN ${a.patientid || "—"}`}
                      </div>
                    </button>
                    <button onClick={() => openPreview(demo, a.patientid, key)} disabled={working} className={btnGhost}>
                      {working ? "…" : "Preview"}
                    </button>
                    <button onClick={() => printOne(demo, a.patientid, key)} disabled={working} className={btnPrimary}>
                      Print
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Patient search */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search patient by name or MRN…"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-80"
            />
            <button onClick={doSearch} disabled={searching} className={btnPrimary}>
              {searching ? "Searching…" : "Search"}
            </button>
            <span className="text-xs text-gray-400">
              Generate a form for any patient — no appointment required.
            </span>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b">
              {results.length} result{results.length === 1 ? "" : "s"}
            </div>
            <div className="max-h-[68vh] overflow-y-auto divide-y">
              {results.length === 0 && (
                <div className="text-gray-400 text-sm p-8 text-center">
                  Search for a patient to generate an encounter form.
                </div>
              )}
              {results.map((p) => {
                const key = `pat-${p.patientid}`;
                const working = busyKey === key;
                const demo = patientDemographics(p);
                return (
                  <div key={key} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    <button
                      className="flex-1 text-left"
                      onClick={() => openPreview(demo, p.patientid, key)}
                      title="Preview form"
                    >
                      <div className="font-semibold text-sm text-gray-800">
                        {fmtName(p.lastname, p.firstname)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {p.dob ? `DOB ${fmtDateSlash(p.dob)}` : ""}
                        {p.gender ? ` · ${fmtSex(p.gender)}` : ""}
                        {` · MRN ${p.patientid || "—"}`}
                      </div>
                    </button>
                    <button onClick={() => openPreview(demo, p.patientid, key)} disabled={working} className={btnGhost}>
                      {working ? "…" : "Preview"}
                    </button>
                    <button onClick={() => printOne(demo, p.patientid, key)} disabled={working} className={btnPrimary}>
                      Print
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div
            className="bg-white rounded-xl shadow-xl w-[760px] max-w-full max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-5 py-3 border-b">
              <div className="font-semibold text-[#1D2951] mr-auto">{preview.name}</div>
              <button onClick={printPreview} className={btnPrimary}>Print</button>
              <button onClick={() => setPreview(null)} className={`${btn} text-gray-500 hover:bg-gray-100`}>Close</button>
            </div>
            <div className="flex-1 overflow-auto bg-[#5c6470] p-4 flex justify-center">
              <iframe
                title="Encounter form preview"
                srcDoc={buildDocument([buildSheet(preview)], preview.name)}
                style={{ width: "8.5in", minWidth: "8.5in", height: "11in", border: "none", background: "#fff", boxShadow: "0 10px 40px rgba(0,0,0,.35)" }}
              />
            </div>
          </div>
        </div>
      )}

      <ToastMessage open={toast.open} message={toast.message} severity={toast.severity} handleClose={closeToast} />
    </div>
    </SideNavLayout>
  );
};

export default PrintNotes;
